'use client';
import { GridColFullWidth, GridContainerColClasses, ScreenHorizontalPadding } from '@constants/layout';
import { SocialProofType } from '@lib/sanity/queries/modules/socialProof/types';
import Container from '@ui/Container/Container';
import Slider from '@ui/Slider/Slider';
import Text, { TextVariant } from '@ui/Text/Text';
import VisualsImageClient from '@ui/Visuals/Image/VisualsImageClient';
import cn from '@utils/cn';

type SocialProofModuleProps = {
  data?: SocialProofType | null;
  isPricing?: boolean;
  textVariant?: TextVariant;
  isInline?: boolean;
};

export default function SocialProofModule({
  data,
  isPricing = false,
  textVariant = TextVariant.TextBodySM,
  isInline = false,
}: SocialProofModuleProps) {
  if (!data) {
    return null;
  }
  // Calculate max height from image dimensions
  const maxHeight =
    data.images?.reduce((max, image) => {
      const height = image.asset?.metadata?.dimensions?.height ?? 0;
      return Math.max(max, height);
    }, 0) ?? 0;

  const items = data.images?.map((image, i) => {
    const width = image.asset?.metadata?.dimensions?.width ?? undefined;
    return (
      <VisualsImageClient
        key={i}
        style={{ width }}
        className="h-auto object-contain md:h-auto"
        image={{ image, alt: image.alt }}
        isEagerLoad={data.index === 0}
      />
    );
  });

  return (
    <Container theme={data.theme} className="bg-background" removeContainer>
      <div
        className={cn('flex w-full flex-col text-center', {
          'pt-8': !isPricing,
        })}
      >
        <Text
          className={cn(ScreenHorizontalPadding, 'w-full', {
            'justify-center': !isPricing,
            'pb-6': !isInline,
            'pb-8': isInline,
          })}
          content={data.title?.value}
          variant={textVariant}
        />

        <div
          className={cn('flex w-full', { 'py-8': !isInline, 'pb-8': isInline })}
          style={{ minHeight: maxHeight > 0 ? `${maxHeight}px` : 'auto' }}
        >
          {data.hasAnimation ? (
            <Slider speed={data.animationSpeed} items={items} />
          ) : (
            <div className={cn(GridContainerColClasses, 'w-full')}>
              <div
                className={cn(
                  GridColFullWidth,
                  ScreenHorizontalPadding,
                  'flex flex-row flex-wrap items-center justify-center gap-8',
                )}
              >
                {items}
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}
